import * as z from "zod/v4";

export enum LcgClassifyMode {
  CLASSIFY = "classify",
  RECLASSIFY = "reclassify",
}

export const LcgClassifyModeSchema = z.enum([
  LcgClassifyMode.CLASSIFY,
  LcgClassifyMode.RECLASSIFY,
]);

export const LCG_CLASSIFY_MODE_LABELS: Record<LcgClassifyMode, string> = {
  [LcgClassifyMode.CLASSIFY]: "Classify - Missing HS codes",
  [LcgClassifyMode.RECLASSIFY]: "Reclassify - Missing & existing HS codes",
};

export enum LcgProductScope {
  ACTIVE_DRAFT_WITH_INVENTORY = "active_draft_with_inventory",
  ACTIVE_DRAFT_ALL = "active_draft_all",
}

export const LcgProductScopeSchema = z.enum([
  LcgProductScope.ACTIVE_DRAFT_WITH_INVENTORY,
  LcgProductScope.ACTIVE_DRAFT_ALL,
]);

export const LCG_PRODUCT_SCOPE_LABELS: Record<LcgProductScope, string> = {
  [LcgProductScope.ACTIVE_DRAFT_WITH_INVENTORY]:
    "Active & Draft where inventory > 0",
  [LcgProductScope.ACTIVE_DRAFT_ALL]: "Active & Draft - All",
};

const classificationRunSchema_ = z.object({
  completedAt: z.date(),
  totalClassified: z.number(),
  totalSkipped: z.number(),
  totalFailed: z.number(),
  totalProductsProcessed: z.number(),
});
export const ClassificationRunSchema: z.ZodType<ClassificationRun> =
  classificationRunSchema_;
export interface ClassificationRun
  extends z.infer<typeof classificationRunSchema_> {}

const landedCostSettingsSchema = z.object({
  enabled: z.boolean(),
  landedCostFee: z.number().nullish(),
  classificationRun: ClassificationRunSchema.optional(),
  /**
   * When true, the merchant purchases shipping labels outside of Redo's OMS
   * (e.g. via ShipStation) and relies on Zonos UPS Unlock. In this mode we
   * commit the landed cost to Zonos at order-ingestion time using the
   * shopify order id as the `accountOrderNumber`, so that a label tagged
   * `zx-{shopifyOrderId}` on UPS can be matched back to the committed LCG.
   */
  purchaseLabelsOutsideOms: z.boolean().default(false),
  // stored UPS account/shipping number required to enable UPS Unlock
  upsShippingNumber: z.string().optional(),
  /**
   * When true, the merchant-facing International → Markets settings page is
   * exposed for this team. Admin-controlled gate set on the LCG admin page;
   * the merchant nav reads `team.settings.landedCost.enableMarkets`.
   */
  enableMarkets: z.boolean().default(false),
  /**
   * When true, fulfill international orders using LCG (DDP) even when the
   * customer did NOT pay landed cost at checkout. By default Redo only ships
   * LCG for orders that paid duties at checkout; this admin-controlled toggle
   * is an opt-in escape hatch for merchants who want LCG applied to all
   * international fulfillments regardless of checkout payment.
   */
  fulfillUsingLcgAlways: z.boolean().default(false),
});

export const LandedCostSettingsSchema: z.ZodType<LandedCostSettings> =
  landedCostSettingsSchema;
export type LandedCostSettings = z.infer<typeof landedCostSettingsSchema>;

export const LANDED_COST_FEE_DEFAULT = 200;

export const landedCostSettingsDefault: LandedCostSettings = {
  enabled: false,
  // $2.00
  landedCostFee: LANDED_COST_FEE_DEFAULT,
  purchaseLabelsOutsideOms: false,
  enableMarkets: false,
  fulfillUsingLcgAlways: false,
};
